import { parseSignInNotice, type SignInNotice } from "@lifter/hub-ui/browser-shared";
import { type FormEvent, useEffect, useRef, useState } from "react";
import { useMeQuery } from "../api/me";
import { Button } from "../components/ui/button";
import { Callout } from "../components/ui/callout";
import { TextField } from "../components/ui/form";
import { LiveRegion } from "../components/ui/live-region";
import { Stack } from "../components/ui/stack";
import { validateEmail } from "../lib/validation";
import { usePortalPath } from "../routing/usePortalPath";
import { AuthFormError, AuthPageFrame } from "./AuthPageFrame";
import { authClient } from "./auth-client";

export function LoginPage() {
  const { replace } = usePortalPath();
  const me = useMeQuery();

  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState<string | undefined>();
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | undefined>();
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [notice, setNotice] = useState<SignInNotice | undefined>();

  // Read the reason once, from the live address rather than the router's
  // snapshot: the snapshot is only resynced when the store gains its first
  // subscriber, so during this page's first render it can still describe the
  // location the app booted on. Claiming it here also drops it from the URL,
  // because the reason describes a navigation that already happened — a
  // reload is a fresh visit, not a repeat of that failure. Only the reason is
  // taken; the rest of the address belongs to whoever put it there.
  const claimedReason = useRef(false);
  useEffect(() => {
    if (claimedReason.current) return;
    claimedReason.current = true;
    const address = new URL(window.location.href);
    const arrived = parseSignInNotice(address.search);
    if (!arrived) return;
    setNotice(arrived);
    address.searchParams.delete("reason");
    replace(`${address.pathname}${address.search}${address.hash}`);
  }, [replace]);

  // A live session has no business on the login page — bounce home.
  useEffect(() => {
    if (me.isSuccess) replace("/");
  }, [me.isSuccess, replace]);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitting) return;
    setServerError(undefined);
    setNotice(undefined);
    const trimmed = email.trim();
    const validationError = validateEmail(trimmed);
    setEmailError(validationError ?? undefined);
    if (validationError) {
      return;
    }
    setSubmitting(true);
    const { error } = await authClient.signIn.magicLink({ email: trimmed, callbackURL: "/" });
    setSubmitting(false);
    if (error) {
      setServerError(error.message ?? "Could not send the sign-in link. Try again.");
      return;
    }
    setMagicLinkSent(true);
  }

  if (magicLinkSent) {
    return (
      <AuthPageFrame focusTitle title="Check your email">
        <LiveRegion>
          <Stack gap="group">
            <p className="text-sm text-[var(--color-text-secondary)]">
              If an account exists for <span className="font-bold">{email.trim()}</span>, you’ll
              receive a sign-in link.
            </p>
            <Button onClick={() => setMagicLinkSent(false)} variant="secondary">
              Back to sign in
            </Button>
          </Stack>
        </LiveRegion>
      </AuthPageFrame>
    );
  }

  return (
    <AuthPageFrame focusTitle={Boolean(notice)} title="Sign in">
      <LiveRegion>
        {notice ? (
          <Callout head={notice.title} variant="warn">
            {notice.message}
          </Callout>
        ) : null}
      </LiveRegion>
      <form className="flex flex-col gap-5" noValidate onSubmit={submit}>
        <TextField
          autoComplete="email"
          disabled={submitting}
          error={emailError}
          label="Email"
          onChange={setEmail}
          placeholder="you@company.com"
          type="email"
          validate={validateEmail}
          value={email}
        />
        <AuthFormError message={serverError} />
        <Button disabled={submitting} loading={submitting} type="submit" variant="primary">
          {submitting ? "Sending…" : "Email me a sign-in link"}
        </Button>
      </form>
    </AuthPageFrame>
  );
}
